import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '../../components/DashboardHeader';
import { FaHome, FaUser, FaHeart, FaEnvelope, FaCog } from 'react-icons/fa';

const PageContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f8f9fa;
`;

const Sidebar = styled.aside`
  width: 250px;
  background: #34574A;
  color: white;
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 40px;
`;

const Avatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin-bottom: 16px;
`;

const UserName = styled.h3`
  font-size: 1.2rem;
  margin: 0;
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 40px 20px;
`;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  background: #34574A;
  padding: 32px;
  color: white;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 8px 0 0;
  opacity: 0.8;
  font-size: 1rem;
`;

const Form = styled.form`
  padding: 32px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    border-color: #34574A;
    outline: none;
    box-shadow: 0 0 0 3px rgba(52, 87, 74, 0.1);
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;

  &:focus {
    border-color: #34574A;
    outline: none;
    box-shadow: 0 0 0 3px rgba(52, 87, 74, 0.1);
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%234A5568' stroke-width='2'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: 40px;

  &:focus {
    border-color: #34574A;
    outline: none;
    box-shadow: 0 0 0 3px rgba(52, 87, 74, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 32px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &.secondary {
    background: white;
    border: 2px solid #e2e8f0;
    color: #4a5568;

    &:hover {
      background: #f7fafc;
      border-color: #cbd5e0;
    }
  }

  &.primary {
    background: #34574A;
    border: none;
    color: white;

    &:hover {
      background: #2a4539;
    }
  }
`;

const NewProperty = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    location: '',
    bedrooms: '',
    bathrooms: '',
    area: '',
    propertyType: '',
  });

  const sidebarItems = [
    { icon: <FaHome />, text: 'Início', active: true },
    { icon: <FaUser />, text: 'Perfil' },
    { icon: <FaHeart />, text: 'Favoritos' },
    { icon: <FaEnvelope />, text: 'Mensagens' },
    { icon: <FaCog />, text: 'Configurações' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form Data:', formData);
    navigate('/dashboard');
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <PageContainer>
      <Sidebar>
        <UserInfo>
          <Avatar src="https://placehold.co/100x100" alt="Avatar do usuário" />
          <UserName>João Silva</UserName>
        </UserInfo>
        <Nav>
          {sidebarItems.map((item, index) => (
            <NavItem key={index} className={item.active ? 'active' : ''}>
              {item.icon}
              <span>{item.text}</span>
            </NavItem>
          ))}
        </Nav>
      </Sidebar>
      <MainContent>
        <DashboardHeader />
        <ContentWrapper>
          <FormContainer>
            <Header>
              <Title>Adicionar Nova Propriedade</Title>
              <Subtitle>Preencha os detalhes da propriedade abaixo</Subtitle>
            </Header>

            <Form onSubmit={handleSubmit}>
              <FormGrid>
                <InputGroup className="full-width">
                  <Label htmlFor="title">Título da Propriedade</Label>
                  <Input
                    id="title"
                    name="title"
                    type="text"
                    placeholder="Ex: Apartamento moderno no centro"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>

                <InputGroup className="full-width">
                  <Label htmlFor="description">Descrição</Label>
                  <TextArea
                    id="description"
                    name="description"
                    placeholder="Descreva os detalhes da propriedade..."
                    value={formData.description}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="price">Preço (€)</Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.price}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="propertyType">Tipo de Propriedade</Label>
                  <Select
                    id="propertyType"
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione o tipo</option>
                    <option value="apartamento">Apartamento</option>
                    <option value="casa">Casa</option>
                    <option value="studio">Studio</option>
                    <option value="condominio">Condomínio</option>
                  </Select>
                </InputGroup>

                <InputGroup className="full-width">
                  <Label htmlFor="location">Localização</Label>
                  <Input
                    id="location"
                    name="location"
                    type="text"
                    placeholder="Endereço completo"
                    value={formData.location}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="bedrooms">Quartos</Label>
                  <Input
                    id="bedrooms"
                    name="bedrooms"
                    type="number"
                    min="0"
                    placeholder="Número de quartos"
                    value={formData.bedrooms}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="bathrooms">Casas de banho</Label>
                  <Input
                    id="bathrooms"
                    name="bathrooms"
                    type="number"
                    min="0"
                    placeholder="Número de casas de banho"
                    value={formData.bathrooms}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="area">Área (m²)</Label>
                  <Input
                    id="area"
                    name="area"
                    type="number"
                    min="0"
                    placeholder="Área total em m²"
                    value={formData.area}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>
              </FormGrid>

              <ButtonGroup>
                <Button type="button" className="secondary" onClick={handleCancel}>
                  Cancelar
                </Button>
                <Button type="submit" className="primary">
                  Salvar Propriedade
                </Button>
              </ButtonGroup>
            </Form>
          </FormContainer>
        </ContentWrapper>
      </MainContent>
    </PageContainer>
  );
};

export default NewProperty;